import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router';
import { ApiError } from '@/api/client';
import { ErrorBoundary } from '@/components/shell/ErrorBoundary';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from '@/stores/theme';
import App from '@/App';
import '@/styles/theme.css';

// 111-frontend-architecture.md §3.2 — the global defaults, and why they differ from
// the library's:
//
//   refetchOnWindowFocus: false — that default exists to catch changes made by OTHER
//     users. There are none. Leaving it on refetches the whole tree every time you
//     alt-tab back from your rulebook PDF.
//   retry: 5xx only — a 401/403/404/422 does not get better by asking again.
//   mutations.retry: false — retries are the OUTBOX's job (M2), never Query's. If both
//     retried, a flaky network would produce duplicate writes.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: (count, error) => error instanceof ApiError && error.status >= 500 && count < 2,
    },
    mutations: { retry: false },
  },
});

const root = document.getElementById('root');
if (root === null) throw new Error('#root is missing from index.html');

createRoot(root).render(
  <StrictMode>
    <ErrorBoundary level="root">
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
          <Toaster />
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
